import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../lib/api';
import { CONFIDENCE, EMAILS, HELPER_LINE, PERSONAS, PREDICT, QUESTIONS, REDACT_NOTE, ROUNDS, type Option, type Question } from './liveContent';
import { Icon } from './liveIcons';
import './live.css';

/**
 * Public audience app for a presented talk (/live/:code). No account: a random token on this device
 * identifies the person to the server, which stores only its hash. The presenter's deck moves the
 * session between phases; this page follows over /box/live/ws (with a polling fallback).
 */

type Phase = 'lobby' | 'survey' | 'results' | 'predict' | 'quiz' | 'redact' | 'report' | 'group' | 'signup';
export interface Group { n: number; inside: Record<string, number>; levels: Record<string, string>; shadow: number; accuracy: number }
interface LiveState { run?: number; phase: Phase; round: string | null; revealed: boolean; correct: string | null }
interface Me { answered?: boolean; persona?: string | null; votes?: Record<string, unknown> }

// Public assets for this app only (the deck itself is private): published by docs/talks/deck/tools/build.cjs.
const ART = '/live-assets/aic26/img';
const KNOW_MORE = 'https://techedify.com/';

function useFonts() {
  useEffect(() => {
    const id = 'live-fonts';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet'; l.href = '/live-assets/aic26/fonts/fonts.css';
    document.head.append(l);
  }, []);
}

function storage(key: string) {
  return {
    get: () => { try { return localStorage.getItem(key); } catch { return null; } },
    set: (v: string) => { try { localStorage.setItem(key, v); } catch { /* private mode */ } },
  };
}

export function LivePage() {
  useFonts();
  const code = (useParams().code || '').toLowerCase();
  const tokenStore = useMemo(() => storage(`live:${code}:token`), [code]);
  const [token, setToken] = useState<string | null>(tokenStore.get());
  const [state, setState] = useState<LiveState | null>(null);
  const [me, setMe] = useState<Me>({});
  const [group, setGroup] = useState<Group | null>(null);
  const [error, setError] = useState('');
  const base = `${API_BASE_URL}/box/live/${code}`;

  const call = useCallback(async (path: string, body?: unknown) => {
    const r = await fetch(base + path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { 'content-type': 'application/json', ...(token ? { 'x-live-token': token } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j?.error?.message || 'Something went wrong');
    return j;
  }, [base, token]);

  // Join once per device.
  useEffect(() => {
    if (token) return;
    fetch(`${base}/join`, { method: 'POST' }).then(r => r.json()).then(j => { tokenStore.set(j.token); setToken(j.token); }).catch(() => setError('Could not connect. Check your connection and refresh.'));
  }, [base, token, tokenStore]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try { const j = await call(''); setState(j.state); setMe(j.me || {}); setGroup(j.group || null); setError(''); } catch (e) { setError((e as Error).message); }
  }, [call, token]);

  // Live updates: socket first, polling as a fallback.
  useEffect(() => {
    if (!token) return;
    refresh();
    let ws: WebSocket | null = null, closed = false, retry = 0;
    const wsBase = API_BASE_URL.replace(/^http/, 'ws');
    const open = () => {
      ws = new WebSocket(`${wsBase}/box/live/ws?code=${encodeURIComponent(code)}`);
      ws.onmessage = ev => { try { const m = JSON.parse(ev.data); if (m.t === 'state') setState(m.state); } catch { /* ignore */ } };
      ws.onopen = () => { retry = 0; };
      ws.onclose = () => { if (!closed) setTimeout(open, Math.min(8000, 800 * 2 ** retry++)); };
    };
    open();
    const poll = setInterval(refresh, 8000);
    return () => { closed = true; ws?.close(); clearInterval(poll); };
  }, [code, refresh, token]);

  // The presenter started a fresh session: this device's earlier answers were archived, so start over.
  const run = state?.run ?? 1;
  const seenRun = useRef(run);
  useEffect(() => { if (run !== seenRun.current) { seenRun.current = run; setMe({}); refresh(); } }, [run, refresh]);

  // Phase changes arrive over the socket without data; the group view needs the room's results, so fetch them.
  const phase = state?.phase;
  useEffect(() => { if (phase === 'group') refresh(); }, [phase, refresh]);

  if (!state) return <Shell><div className="lv-center"><div className="lv-spinner" />{error && <p className="lv-err">{error}</p>}</div></Shell>;

  return (
    <Shell>
      {error && <p className="lv-err">{error}</p>}
      <Stage key={run} state={state} me={me} group={group} call={call} refresh={refresh} />
      {/* only on waiting screens: not during the survey, an activity (next word, Spot the AI, redaction), or the final thank-you */}
      {!['signup', 'predict', 'quiz', 'redact'].includes(state.phase) && !(!me.answered && ['lobby', 'survey', 'results'].includes(state.phase)) && <KeepOpen />}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="lv-root"><main className="lv-main">{children}</main></div>;
}

function Stage({ state, me, group, call, refresh }: { state: LiveState; me: Me; group: Group | null; call: (p: string, b?: unknown) => Promise<any>; refresh: () => Promise<void> }) {
  const { phase } = state;
  if (!me.answered && (phase === 'lobby' || phase === 'survey' || phase === 'results')) return <Survey call={call} refresh={refresh} />;
  if (phase === 'predict') return <Predict call={call} mine={me.votes?.predict as string | undefined} refresh={refresh} />;
  if (phase === 'quiz' && state.round && ROUNDS[state.round]) return <Quiz key={state.round} state={state} call={call} mine={me.votes?.[state.round] as string | undefined} refresh={refresh} />;
  if (phase === 'redact') return <Redact call={call} mine={me.votes?.redact as string[] | undefined} refresh={refresh} />;
  if (phase === 'signup') return <Done persona={me.persona} />;
  if (phase === 'group') return <GroupView g={group} />;
  return <Persona persona={me.persona} closed={!me.answered} />;
}

// ───────────────────────── Survey: one question at a time ─────────────────────────
function Survey({ call, refresh }: { call: (p: string, b?: unknown) => Promise<any>; refresh: () => Promise<void> }) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [custom, setCustom] = useState<Record<string, string>>({}); // typed answers, keyed by customKey (teamText…)
  const [word, setWord] = useState('');
  const [open, setOpen] = useState<string | null>(null);              // which explanation is showing
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const q = QUESTIONS[i];
  const last = i === QUESTIONS.length - 1;
  const next = () => { setOpen(null); if (i < QUESTIONS.length - 1) setI(i + 1); };
  const pick = (o: Option) => {
    setAnswers(a => ({ ...a, [q.id]: o.id }));
    if (q.allowWord) setWord('');
    if (!o.custom && !last) setTimeout(next, 220);
  };
  const typedOk = (x: Question) => answers[x.id] !== 'custom' || !x.customKey || !!(custom[x.customKey] || '').trim();
  const submit = async () => {
    setBusy(true); setErr('');
    const chosen = word.trim() || answers.word || '';
    try { await call('/survey', { ...answers, ...custom, word: chosen }); await refresh(); } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const canSubmit = last && (answers.word || word.trim()) && QUESTIONS.slice(0, -1).every(x => answers[x.id] && typedOk(x));
  const customOn = answers[q.id] === 'custom' && !!q.customKey;
  return (
    <section className="lv-card lv-q" key={q.id}>
      <div className="lv-progress"><i style={{ width: `${((i + 1) / QUESTIONS.length) * 100}%` }} /></div>
      <div className="lv-qtop"><span className="lv-step">{i + 1} of {QUESTIONS.length}</span>{i > 0 && <button className="lv-link" onClick={() => { setOpen(null); setI(i - 1); }}>← Back</button>}</div>
      <div className="lv-qhead">
        <h1 className="lv-h">{q.prompt}</h1>
        {q.info && <button className={`lv-infobtn ${open === 'q' ? 'on' : ''}`} aria-expanded={open === 'q'} aria-label="What does this mean?" title={q.info} onClick={() => setOpen(open === 'q' ? null : 'q')}><Icon name="info" size={20} /></button>}
      </div>
      {open === 'q' && q.info && <p className="lv-explain">{q.info}</p>}
      {q.hint && <p className="lv-hint">{q.hint}</p>}
      <div className={`lv-opts ${q.options.length > 5 ? 'grid' : ''}`}>
        {q.options.map(o => (
          <div key={o.id} className={`lv-optw ${o.info ? 'has-info' : ''}`}>
            <button className={`lv-opt ${answers[q.id] === o.id && !word ? 'on' : ''}`} onClick={() => pick(o)}>
              <span className="lv-ic"><Icon name={o.icon} size={24} /></span>
              <span className="lv-optlabel">{o.label}{open === o.id && o.info && <small>{o.info}</small>}</span>
            </button>
            {o.info && <button className={`lv-infobtn sm ${open === o.id ? 'on' : ''}`} aria-expanded={open === o.id} aria-label={`What does "${o.label}" mean?`} title={o.info} onClick={() => setOpen(open === o.id ? null : o.id)}><Icon name="info" size={17} /></button>}
          </div>
        ))}
      </div>
      {customOn && (
        <>
          <input className="lv-input" autoFocus maxLength={q.id === 'team' ? 40 : 60} placeholder={q.customPlaceholder} value={custom[q.customKey!] || ''}
            onChange={e => setCustom(c => ({ ...c, [q.customKey!]: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && typedOk(q)) next(); }} />
          {!last && <button className="lv-primary" disabled={!typedOk(q)} onClick={next}>Next</button>}
        </>
      )}
      {q.allowWord && (
        <input className="lv-input" placeholder="Or type one word" maxLength={20} value={word} onChange={e => setWord(e.target.value.replace(/\s+/g, ''))} />
      )}
      {last && <button className="lv-primary" disabled={!canSubmit || busy} onClick={submit}>{busy ? 'Sending…' : 'Send my answers'}</button>}
      {err && <p className="lv-err">{err}</p>}
      <p className="lv-fine">Anonymous. No name, no email. Your answers shape today's session.</p>
    </section>
  );
}

function Persona({ persona, closed }: { persona?: string | null; closed?: boolean }) {
  const p = persona ? PERSONAS[persona] : null;
  if (!p) return (
    <section className="lv-card lv-persona lv-waitcard">
      <img className="lv-waitart" src={`${ART}/live-wait.webp`} alt="" />
      <p className="lv-kicker">{closed ? 'The survey has closed' : 'Hang on'}</p>
      <h1 className="lv-h">Please keep this tab open</h1>
      <p className="lv-lead">There's more coming: a game, a quick challenge and the group's results. This page changes on its own when it's your turn.</p>
    </section>
  );
  return (
    <section className="lv-card lv-persona">
      <p className="lv-kicker">You are</p>
      <div className="lv-orb"><img src={`${ART}/persona-${persona}.webp`} alt="" /></div>
      <h1 className="lv-h">{p.name}</h1>
      <p className="lv-what">{p.what}</p>
      <p className="lv-lead">{p.line}</p>
      <p className="lv-tip">{p.tip}</p>
    </section>
  );
}

/** Floating reminder on every screen until the end: look at the presenter, and don't close the tab. */
function KeepOpen() {
  return (
    <div className="lv-keep" role="status">
      <span className="lv-keep-ic"><Icon name="alert" size={22} /></span>
      <div><b>Keep this tab open until the end of the talk.</b><span>Look at the presenter's screen. This page changes when it's your turn.</span></div>
    </div>
  );
}

// ───────────────────────── This group, today (slide 35) ─────────────────────────
const PILLARS: Array<[string, string, string]> = [
  ['Policy', 'Clear rules for using AI at work, and people knowing them.', 'agree simple written rules on which AI tools are allowed and what can go into them'],
  ['Tools', 'Using AI accounts the company provides, not personal ones.', 'give everyone one approved AI tool, so work stops going into personal accounts'],
  ['Skills', 'How people learned AI: on their own, or through company training.', 'run regular, hands-on AI training for everyone'],
  ['Process', 'Whether teams use AI in agreed ways, and check unusual requests safely.', 'agree how teams use AI, and add a call-back step for unusual requests'],
];
const LEVELS: Array<[string, string, string]> = [
  ['Curious', '#9AA3B5', 'People try AI on their own. No shared rules or tools yet.'],
  ['Experimenting', '#F2A63B', 'Some teams test AI, with early rules or training.'],
  ['Governed', '#0E7C66', 'Approved tools, clear rules, and trained people.'],
  ['Transformed', '#5B3FA0', 'AI is part of everyday work across teams.'],
];
const levelOf = (v: number) => (v < 0.75 ? 'Curious' : v < 1.5 ? 'Experimenting' : v < 2.25 ? 'Governed' : 'Transformed');

function GroupView({ g }: { g: Group | null }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!g) return <section className="lv-card lv-center"><div className="lv-spinner" /><p className="lv-lead">Adding up everyone's answers…</p></section>;
  const rows = PILLARS.map(([k, what, action]) => ({ k, what, action, v: g.inside[k] ?? 0, lvl: g.levels[k] || levelOf(g.inside[k] ?? 0) }));
  const avg = rows.reduce((a, r) => a + r.v, 0) / rows.length;
  const overall = levelOf(avg);
  const sorted = [...rows].sort((a, b) => a.v - b.v);
  const weak = sorted[0], strong = sorted[sorted.length - 1];
  const meaning = (LEVELS.find(l => l[0] === overall) || LEVELS[0])[2];
  const col = (lvl: string) => (LEVELS.find(l => l[0] === lvl) || LEVELS[0])[1];
  return (
    <section className="lv-card lv-group">
      <p className="lv-kicker">This group, today</p>
      <h1 className="lv-h">Where we stand</h1>
      <p className="lv-fine lv-left">{g.n} {g.n === 1 ? "person's" : "people's"} answers, added up. Nobody's answers are shown on their own.</p>
      <div className="lv-standing">
        <p>Overall, this group is <b style={{ color: col(overall) }}>{overall}</b>. {meaning}</p>
        {g.n > 0 && strong.k !== weak.k && <p>Strongest area: <b>{strong.k}</b> ({strong.lvl}). The place to start is <b>{weak.k}</b> ({weak.lvl}): {weak.action}.</p>}
        {g.shadow > 0 && <p>{g.shadow}% use AI every week on personal accounts without knowing the rules.</p>}
      </div>
      <div className="lv-pillars">
        {rows.map(r => (
          <div className="lv-pillar" key={r.k}>
            <div className="lv-ptop">
              <b>{r.k}</b>
              <span className="lv-plvl" style={{ background: col(r.lvl) }}>{r.lvl}</span>
              <button className={`lv-infobtn sm-inline ${open === r.k ? 'on' : ''}`} aria-expanded={open === r.k} aria-label={`What is ${r.k}?`} title={r.what} onClick={() => setOpen(open === r.k ? null : r.k)}><Icon name="info" size={17} /></button>
            </div>
            {open === r.k && <p className="lv-pwhat">{r.what}</p>}
            <div className="lv-ptrack"><i style={{ width: `${Math.max(4, Math.min(100, (r.v / 3) * 100))}%`, background: col(r.lvl) }} /></div>
          </div>
        ))}
      </div>
      <details className="lv-levels">
        <summary>What do the levels mean?</summary>
        <ul>{LEVELS.map(([n, c, t]) => <li key={n}><span style={{ background: c }} /> <b>{n}</b> {t}</li>)}</ul>
      </details>
    </section>
  );
}

// ───────────────────────── Next-word game (slide 8) ─────────────────────────
function Predict({ call, mine, refresh }: { call: (p: string, b?: unknown) => Promise<any>; mine?: string; refresh: () => Promise<void> }) {
  const [sel, setSel] = useState(mine);
  const choose = async (w: string) => { setSel(w); try { await call('/vote', { kind: 'predict', choice: w }); refresh(); } catch { /* keep local */ } };
  return (
    <section className="lv-card">
      <p className="lv-kicker">Be the keyboard</p>
      <h1 className="lv-h">Which word comes next?</h1>
      <div className="lv-phone"><div className="lv-bubble">Great meeting today! Are we still on for the site visit tomorrow morning?</div><div className="lv-typing">Yes! See you <b>{sel || '…'}</b><i /></div></div>
      <div className="lv-opts grid">
        {PREDICT.map(w => <button key={w} className={`lv-opt center ${sel === w ? 'on' : ''}`} onClick={() => choose(w)}>{w}</button>)}
      </div>
      {sel && <p className="lv-waitline"><span className="lv-dot" />Locked in. Watch the screen.</p>}
    </section>
  );
}

// ───────────────────────── Spot the AI ─────────────────────────
function Quiz({ state, call, mine, refresh }: { state: LiveState; call: (p: string, b?: unknown) => Promise<any>; mine?: string; refresh: () => Promise<void> }) {
  const r = ROUNDS[state.round as string];
  const [choice, setChoice] = useState<string | undefined>(mine);
  const [conf, setConf] = useState('fairly');
  const [err, setErr] = useState('');
  const send = async (c: string, cf: string) => {
    setChoice(c); setErr('');
    try { await call('/vote', { kind: state.round, choice: c, confidence: cf }); refresh(); } catch (e) { setErr((e as Error).message); }
  };
  const revealed = state.revealed;
  const right = revealed && choice === state.correct;
  return (
    <section className="lv-card">
      <p className="lv-kicker">{r.title}</p>
      <h1 className="lv-h">{r.question}</h1>
      {state.round === 'r2' && (
        <div className="lv-mails">{EMAILS.map(m => <div key={m.label} className="lv-mail"><b>{m.label} · {m.subject}</b><p>{m.body}</p></div>)}</div>
      )}
      <div className={`lv-media n${r.media.length}`}>
        {r.media.map(m => (
          <figure key={m.src}>
            {m.label && <figcaption>{m.label}</figcaption>}
            {m.kind === 'image' && <img src={m.src} alt="" />}
            {m.kind === 'audio' && <audio src={m.src} controls preload="none" />}
            {m.kind === 'video' && <video src={m.src} controls playsInline preload="metadata" />}
            {m.caption && <span className="lv-cap">{m.caption}</span>}
          </figure>
        ))}
      </div>
      <div className="lv-opts row">
        {r.options.map(o => (
          <button key={o} disabled={revealed} className={`lv-opt center ${choice === o ? 'on' : ''} ${revealed && o === state.correct ? 'correct' : ''}`} onClick={() => send(o, conf)}>{o}</button>
        ))}
      </div>
      {!revealed && (
        <div className="lv-conf"><span>How sure are you?</span>{CONFIDENCE.map(c => <button key={c.id} className={conf === c.id ? 'on' : ''} onClick={() => { setConf(c.id); if (choice) send(choice, c.id); }}>{c.label}</button>)}</div>
      )}
      {!revealed && choice && <p className="lv-waitline"><span className="lv-dot" />Locked in. You can change it until the answer is shown.</p>}
      {revealed && <div className={`lv-result ${right ? 'ok' : 'no'}`}><b>{choice ? (right ? 'You got it.' : 'It fooled you.') : 'Answer'}</b><p>{r.reveal}</p></div>}
      {err && <p className="lv-err">{err}</p>}
    </section>
  );
}

// ───────────────────────── Take the details out (slide 24) ─────────────────────────
function Redact({ call, mine, refresh }: { call: (p: string, b?: unknown) => Promise<any>; mine?: string[]; refresh: () => Promise<void> }) {
  const [content, setContent] = useState<Record<string, string>>({});
  const [sel, setSel] = useState<string[]>(mine || []);
  const [sent, setSent] = useState(!!mine);
  const loaded = useRef(false);
  useEffect(() => { if (loaded.current) return; loaded.current = true; call('').then(j => setContent(j.content?.redact || {})).catch(() => undefined); }, [call]);
  const toggle = (id: string) => { if (sent) return; setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id])); };
  const submit = async () => { await call('/vote', { kind: 'redact', items: sel }); setSent(true); refresh(); };
  return (
    <section className="lv-card">
      <p className="lv-kicker">Take the details out</p>
      <h1 className="lv-h">Tap only what shouldn't go into a public AI tool</h1>
      <p className="lv-hint">Careful: some details are fine to keep, and the AI needs them to help.</p>
      <p className="lv-note">
        {REDACT_NOTE.map((part, k) => (typeof part === 'string' ? <span key={k}>{part}</span> : (
          <button key={k} className={`lv-tok ${sel.includes(part.id) ? 'on' : ''}`} onClick={() => toggle(part.id)}>{content[part.id] || '…'}</button>
        )))}
      </p>
      {!sent ? <button className="lv-primary" onClick={submit}>Done ({sel.length} marked)</button> : <p className="lv-waitline"><span className="lv-dot" />Sent. Watch the screen to see what everyone picked.</p>}
    </section>
  );
}

// ───────────────────────── End: thank you, then techedify.com ─────────────────────────
function Done({ persona }: { persona?: string | null }) {
  const [left, setLeft] = useState(6);
  useEffect(() => {
    if (left <= 0) { window.location.assign(KNOW_MORE); return; }
    const t = setTimeout(() => setLeft(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  const p = persona ? PERSONAS[persona] : null;
  return (
    <section className="lv-card lv-persona">
      <p className="lv-kicker">Thank you for joining</p>
      {p && <div className="lv-orb"><img src={`${ART}/persona-${persona}.webp`} alt="" /></div>}
      <h1 className="lv-h">{p ? p.name : 'See you soon'}</h1>
      <p className="lv-lead">Want to know more? We're taking you to techedify.com in {Math.max(0, left)}…</p>
      <a className="lv-primary lv-link-btn" href={KNOW_MORE}>Go to techedify.com now</a>
    </section>
  );
}

export { HELPER_LINE };
